// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {isNil as _isNil} from 'lodash';
import React, {FC, useEffect, useState} from 'react';

import * as Filter from '../../util/filters';
import * as FilterTypes from '../../util/filterTypes';
import * as RunTypes from '../../util/runTypes';
import {EditableFilterContextProvider} from './FilterContext';
import {FilterEditable} from './FilterEditable';
import {FilterKeySelectorCreatorProps} from './FilterKeySelector';
import {FilterValueSelectorCreatorProps} from './FilterValueSelector';
import {handleFilterOpChange} from './logic';

interface FilterListProps {
  filters: FilterTypes.RootFilter<FilterTypes.FilterKey>['filters'][0];
  index: number;
  canAdd: boolean;
  pushFilter(filter: FilterTypes.Filter<FilterTypes.FilterKey>): void;
  deleteFilter(index: number): void;
  setFilter(
    index: number,
    filter: FilterTypes.IndividualFilter<FilterTypes.FilterKey>
  ): void;

  filterKeySelector(props: FilterKeySelectorCreatorProps): React.ReactNode;
  filterValueSelector(
    props: FilterValueSelectorCreatorProps<FilterTypes.FilterKey>
  ): React.ReactNode;
}

const FilterList: FC<FilterListProps> = React.memo(
  ({
    filters: rootFilters,
    canAdd,
    pushFilter,
    deleteFilter,
    setFilter,
    filterKeySelector,
    filterValueSelector,
  }) => {
    const [addedFilterIndex, setAddedFilterIndex] = useState(-1);
    const filters = rootFilters.filters;
    let counter = 0;
    useEffect(() => {
      if (filters.length === 0) {
        pushFilter({
          key: {section: 'run', name: ''},
          op: '=',
          value: '',
          disabled: true,
        });
      }
    }, [filters.length, pushFilter]);
    /**
     * Question: what is EditableFiltersContextProvider and why does it duplicate a bunch of functionality in FilterEditable?
     *
     * Answer: It currently exists only to support the Infinite Loading Tags filter. It's written with an eye toward eventually being the single source of truth for all editable filters.
     *
     * What I like about the previous pattern: a good design aspect of the previous pattern is that the functions defined as props take a powerful tool (the filters "library" which exists as a pretty base level abstraction in our code) and hide some of the complexity behind a more semantically informative set of functions that protect downstream code from having to know about the exact internals of the filter library. I also like that it pre-applies information (in this case the filter out of the map iteration) so that downstream we don't have to do a bunch of redundant data passing.
     *
     * What I don't love is that if you don't know exactly how filters work there's pretty heavy lift to go upstream through N layers of components which all drill these functions down in order to find out what's going on. There's also a minimum of explanation here at the root to help someone make educated decisions about how/where to modify code. My thoughts for the context provider is that it can be a single point of translation between the root filter library code and any components that need to express changes in terms of operations built out of that root level library. I would like it to be accessible _without_ prop drilling so that it's easier to uncover and reason about.
     *
     * WARNING: the types are (maybe) a lie! I'm operating a narrow slice of the complexity described by the underlying filter types and I don't know that they're constructed such that narrowing them is easy to do (which is why the previous code casts liberally). It will need to be revisited if more use cases come to be included in EditableFiltersContextProvider
     */
    return (
      <Tailwind>
        <div className="flex flex-col gap-8">
          <EditableFilterContextProvider
            actions={{
              deleteFilter,
              setFilter,
            }}
            filters={
              filters.filter(f => Filter.isIndividual(f)) as Array<
                FilterTypes.IndividualFilter<FilterTypes.FilterKey>
              >
            }>
            {filters.map((filter, i) => {
              // TODO: display compound filters
              if (!Filter.isIndividual(filter)) {
                return null;
              }
              const currentCounter = counter++;
              return (
                <FilterEditable
                  key={`filter=${currentCounter}`}
                  filterIndex={currentCounter}
                  justAdded={addedFilterIndex === currentCounter}
                  filter={filter}
                  setFilterDisabled={(disabled: boolean) => {
                    const f: FilterTypes.IndividualFilter<FilterTypes.FilterKey> =
                      filter as FilterTypes.IndividualFilter<FilterTypes.FilterKey>;
                    setFilter(i, {...f, disabled});
                  }}
                  setFilter={filt => setFilter(i, filt)}
                  setFilterOp={(newFilterOp: FilterTypes.IndividualOp) => {
                    setFilter(i, handleFilterOpChange(filter, newFilterOp));
                  }}
                  setFilterValue={(filterValue: RunTypes.Value) => {
                    setFilter(i, {
                      ...filter,
                      value: filterValue,
                      disabled: _isNil(filterValue),
                    } as FilterTypes.ValueFilter<FilterTypes.FilterKey> &
                      FilterTypes.ToggleableFilter);
                  }}
                  setFilterMeta={(meta: any) => {
                    setFilter(i, {
                      ...filter,
                      meta,
                    } as FilterTypes.ValueFilter<FilterTypes.FilterKey>);
                  }}
                  setFilterMultiValue={(filterValue: RunTypes.Value[]) => {
                    setFilter(i, {
                      ...filter,
                      value: filterValue,
                      disabled: _isNil(filterValue),
                    } as FilterTypes.MultiValueFilter<FilterTypes.FilterKey> &
                      FilterTypes.ToggleableFilter);
                  }}
                  deleteFilter={() => deleteFilter(i)}
                  filterKeySelector={filterKeySelector}
                  filterValueSelector={filterValueSelector}
                />
              );
            })}
            {counter > 0 && <hr className="mb-8 text-moon-250" />}
            {canAdd && (
              <Button
                icon="add-new"
                className="mb-10"
                data-test="filter-add"
                // onClick={() => pushFilter(FilterTypes.defaultNewFilter)}>
                onClick={() => {
                  // pushFilter(defaultNewRunFilter);
                  // TODO: check if it's ok to add this as an empty filter
                  setAddedFilterIndex(filters.length);
                  pushFilter({
                    key: {section: 'run', name: ''},
                    op: '=',
                    value: '',
                    disabled: true,
                  });
                }}>
                New filter
              </Button>
            )}
          </EditableFilterContextProvider>
        </div>
      </Tailwind>
    );
  }
);

export default FilterList;
